import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetLastQuizAttempt } from "@/react/components/Spaces/CourseSpace/Show/Lesson/Content/QuizContent/hooks/useGetLastQuizAttempt";
import { isStructuredCourseSpace } from "@/react/helpers/courseHelpers";
import { useTailwindLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { Layout } from "@circle-react/components/shared/CustomizeUI";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import {
  useCurrentSpaceContext,
  useSpaceResource,
} from "@circle-react/contexts";
import { RailbarContextProvider } from "@circle-react/hooks/chatsV2/useRailbar";
import {
  useCourseSections,
  useLesson,
} from "@circle-react/hooks/courses/useCoursesApi";
import { Modal } from "@circle-react-uikit/ModalV2";
import { usePreviewAsStudent } from "../../Shared/usePreviewAsStudent";
import { Content } from "./Content";
import { Comments } from "./Content/Comments";
import { Files } from "./Content/Files";
import { TOC } from "./Content/TOC";
import { LessonFooter } from "./Footer";
import { LessonHeader } from "./Header";
import { LessonHelloBar } from "./LessonHelloBar";
import { PreviewReleasedToggle } from "./PreviewReleasedToggle";
import { useAutocompleteOnFeaturedMediaCompleted } from "./hooks/useAutocompleteOnFeaturedMediaCompleted";
import { useTrackFeaturedMediaCompletion } from "./hooks/useTrackFeaturedMediaCompletion";

const SIDEBAR_CONTENTS = {
  TOC: "TOC",
  COMMENTS: "COMMENTS",
  FILES: "FILES",
};

const computeDrippedAt = ({
  isLessonPreview,
  isPreviewingAsReleased,
  spaceResource,
  currentSection,
  lesson,
}) => {
  const fakeUnreleasedDate = new Date(
    new Date().setDate(
      new Date().getDate() + (currentSection?.drip_delay ?? 0),
    ),
  );

  if (
    isLessonPreview &&
    !isPreviewingAsReleased &&
    (currentSection?.is_dripped || !lesson?.dripped_at) &&
    isStructuredCourseSpace(spaceResource)
  ) {
    return fakeUnreleasedDate.toISOString();
  }
  return lesson?.dripped_at;
};

export const ShowLesson = () => {
  const messageHashId = (window.location.hash || "").split("#message_")[1];
  const { isLessonPreview } = usePreviewAsStudent();
  const params = useParams();
  const [sidebarContent, setSidebarContent] = useState(null);
  const [isPreviewingAsReleased, setIsPreviewingAsReleased] = useState(false);
  const [lastAttempt, setLastAttempt] = useState(null);
  const isLgScreen = useTailwindLgScreenMediaQuery();
  const hideSidebar = () => setSidebarContent(null);

  const { data: space, isLoading: isCurrentSpaceDataLoading } =
    useCurrentSpaceContext();
  const { data: spaceResource, isLoading: isSpaceResourceLoading } =
    useSpaceResource({
      spaceId: space?.id,
    });
  const {
    data: lesson,
    isLoading: isLessonLoading,
    refetch: refetchLesson,
  } = useLesson({
    courseId: space?.id,
    lessonId: params.lessonId,
    sectionId: params.sectionId,
    options: {
      enabled: !!space && !isCurrentSpaceDataLoading,
      retry: 0,
      onError: e => {
        if (e?.status === 401) {
          window.location.href = "/";
        }
      },
    },
  });

  const { isLoadingLastAttempt } = useGetLastQuizAttempt({
    lesson,
    setLastAttempt,
  });

  const isFeaturedMediaTrackable = Boolean(
    lesson?.featured_media?.content_type?.includes("video/"),
  );

  const shouldEnforceFeaturedMediaCompletion =
    isFeaturedMediaTrackable &&
    lesson?.enforce_featured_media_completion &&
    !lesson?.completed;

  const {
    onFeaturedMediaProgress,
    isFeaturedMediaCompleted,
    hasFeaturedMediaEnded,
  } = useTrackFeaturedMediaCompletion({ lesson });

  const onMediaEnded = useAutocompleteOnFeaturedMediaCompleted({
    lesson,
    shouldEnforceFeaturedMediaCompletion,
    isFeaturedMediaCompleted,
    refetchLesson,
  });

  useEffect(() => {
    if (hasFeaturedMediaEnded) {
      onMediaEnded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasFeaturedMediaEnded is the only relevant dependency
  }, [hasFeaturedMediaEnded]);

  const isCommentsActive = () => sidebarContent === SIDEBAR_CONTENTS.COMMENTS;
  const isTOCActive = () => sidebarContent === SIDEBAR_CONTENTS.TOC;
  const isFilesActive = () => sidebarContent === SIDEBAR_CONTENTS.FILES;

  const isDripped = isLessonPreview
    ? isPreviewingAsReleased
    : lesson?.is_dripped;

  const isCourseCommentsEnabled =
    !isLessonLoading && !!lesson?.comments_enabled && isDripped;

  const shouldShowComments = isCommentsActive() && isCourseCommentsEnabled;
  const shouldShowTOC =
    isTOCActive() || (!isDripped && sidebarContent !== null);
  const shouldShowFiles = isFilesActive() && isDripped;

  const toggleComments = () =>
    setSidebarContent(shouldShowComments ? null : SIDEBAR_CONTENTS.COMMENTS);

  const toggleTOC = () =>
    setSidebarContent(shouldShowTOC ? null : SIDEBAR_CONTENTS.TOC);

  const toggleFiles = () =>
    setSidebarContent(shouldShowFiles ? null : SIDEBAR_CONTENTS.FILES);

  const { data: sectionsData } = useCourseSections(space?.id);
  const currentSection = sectionsData?.records?.find(
    section => section.id == params.sectionId,
  );

  useEffect(() => {
    if (isLessonPreview) {
      setIsPreviewingAsReleased(lesson?.is_dripped);
    }
  }, [isLessonPreview, lesson]);

  const handleSetDefaultTab = () => {
    if (lesson && isLgScreen) {
      if (lesson.default_tab === "comments" && !isCommentsActive()) {
        setSidebarContent(SIDEBAR_CONTENTS.COMMENTS);
      }
      if (lesson.default_tab === "curriculum" && !isTOCActive()) {
        setSidebarContent(SIDEBAR_CONTENTS.TOC);
      }
      if (lesson.default_tab === "files" && !isFilesActive()) {
        setSidebarContent(SIDEBAR_CONTENTS.FILES);
      }
    }
  };

  useEffect(() => {
    if (messageHashId) {
      toggleComments();
    } else {
      handleSetDefaultTab();
    }
  }, [lesson]);

  const previewReleasedToggle = currentSection && lesson && spaceResource && (
    <PreviewReleasedToggle
      lesson={lesson}
      spaceResource={spaceResource}
      dripDelay={currentSection.drip_delay}
      isLessonPreview={isLessonPreview}
      isPreviewingAsReleased={isPreviewingAsReleased}
      setIsPreviewingAsReleased={setIsPreviewingAsReleased}
    />
  );

  const drippedAt = useMemo(
    () =>
      computeDrippedAt({
        isLessonPreview,
        isPreviewingAsReleased,
        spaceResource,
        currentSection,
        lesson,
      }),
    [
      isLessonPreview,
      isPreviewingAsReleased,
      spaceResource,
      currentSection,
      lesson,
    ],
  );

  return (
    <Modal key={params.lessonId} isOpen>
      <Modal.Content size="screen">
        <RailbarContextProvider isCourseSpace>
          <Layout className="grid-areas-course-view-with-hellobar-mobile lg:grid-areas-course-view-with-hellobar !grid-rows-course-view-with-hellobar">
            <LessonHelloBar />
            {isSpaceResourceLoading ? (
              <Layout.Main>
                <div className="flex items-center justify-center">
                  <LoaderRow />
                </div>
              </Layout.Main>
            ) : (
              <>
                <LessonHeader
                  shouldShowTOC={shouldShowTOC}
                  shouldShowFiles={shouldShowFiles}
                  isCourseCommentsEnabled={isCourseCommentsEnabled}
                  shouldShowComments={shouldShowComments}
                  toggleTOC={toggleTOC}
                  toggleComments={toggleComments}
                  toggleFiles={toggleFiles}
                  previewReleasedToggle={previewReleasedToggle}
                  isDripped={isDripped}
                  space={space}
                  lesson={lesson}
                  refetchLesson={refetchLesson}
                />
                {isLessonLoading || !lesson ? (
                  <Layout.Main>
                    <div className="flex items-center justify-center">
                      <LoaderRow />
                    </div>
                  </Layout.Main>
                ) : (
                  <Content
                    lesson={lesson}
                    refetchLesson={refetchLesson}
                    isDripped={isDripped}
                    drippedAt={drippedAt}
                    isLessonPreview={isLessonPreview}
                    onFeaturedMediaProgress={onFeaturedMediaProgress}
                    lastAttempt={lastAttempt}
                    setLastAttempt={setLastAttempt}
                    isLoadingLastAttempt={isLoadingLastAttempt}
                  />
                )}
                <LessonFooter
                  isDripped={isDripped}
                  isFeaturedMediaCompleted={isFeaturedMediaCompleted}
                  shouldEnforceFeaturedMediaCompletion={
                    shouldEnforceFeaturedMediaCompletion
                  }
                  lesson={lesson}
                  refetchLesson={refetchLesson}
                  lastAttempt={lastAttempt}
                  shouldEnforcePassingGrade={
                    lesson?.quiz?.enforce_passing_grade
                  }
                />
                {shouldShowTOC && <TOC hideTOC={hideSidebar} />}
                {shouldShowFiles && isDripped && (
                  <Files onHeaderCloseClick={hideSidebar} />
                )}
                {shouldShowComments && (
                  <Comments lesson={lesson} hideComments={hideSidebar} />
                )}
              </>
            )}
          </Layout>
        </RailbarContextProvider>
      </Modal.Content>
    </Modal>
  );
};
